import React, {
    useCallback,
    useState,
} from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import {
    closestCenter,
    DndContext,
    DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faTimes } from '@fortawesome/free-solid-svg-icons';

import Modal from 'components/Modal';
import ConfirmModal from 'components/Modal/ConfirmModal';
import Button from 'components/inputs/Button';
import {
  LoadingStatusEnum,
  SegmentStatusEnum,
  TransportSegmentModel,
} from 'api/model';
import {
  useTransportSegmentsGetSegmentsForTruckAssignment,
  useTransportSegmentsUpdateSegmentOrder,
} from 'api/transport-segments/transport-segments';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  min-width: 600px;
  max-width: 800px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: ${({ theme }) => theme.colors.foreground.secondary};
  padding: 4px 8px;

  &:hover {
    color: ${({ theme }) => theme.colors.foreground.primary};
  }
`;

const SegmentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 60vh;
  overflow-y: auto;
`;

const SegmentItemWrapper = styled.div<{ isDragging?: boolean }>`
  display: flex;
  align-items: stretch;
  background: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  opacity: ${({ isDragging }) => (isDragging ? 0.5 : 1)};
  transition: opacity 0.2s;
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  cursor: grab;
  color: ${({ theme }) => theme.colors.foreground.secondary};

  &:active {
    cursor: grabbing;
  }
`;

const SegmentContent = styled.div`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SegmentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SegmentTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.background.button };
`;

const StatusBadge = styled.span<{ status: SegmentStatusEnum }>`
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  background: transparent;
  color: ${({ theme }) => theme.colors.background.button };
`;

const RouteInfo = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

const CarsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 8px;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 4px;
`;

const CarItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  padding: 4px 0;
`;

const CarInfo = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const RegNumber = styled.span`
  font-weight: 600;
  font-family: monospace;
`;

const VehicleName = styled.span`
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

const LoadingStatusBadge = styled.span<{ status: LoadingStatusEnum }>`
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  color: white;
  background: ${({ status, theme }) => {
    switch (status) {
        case LoadingStatusEnum.Unloaded:
            return theme.colors.foreground.primary;
        case LoadingStatusEnum.Loaded:
            return theme.colors.foreground.primary;
      case LoadingStatusEnum.NotLoaded:
        default:
            return theme.colors.foreground.secondary;
    }
  }};
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const EmptyState = styled.div`
  padding: 40px;
  text-align: center;
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

interface SortableSegmentItemProps {
  segment: TransportSegmentModel;
}

const SortableSegmentItem: React.FC<SortableSegmentItemProps> = ({
  segment,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: segment.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getStatusText = (status: SegmentStatusEnum): string => {
    switch (status) {
      case SegmentStatusEnum.NotStarted:
        return 'Ej påbörjad';
      case SegmentStatusEnum.InProgress:
        return 'Pågående';
      case SegmentStatusEnum.Completed:
        return 'Klar';
      default:
        return '';
    }
  };

  const getLoadingStatusText = (status: LoadingStatusEnum): string => {
    switch (status) {
      case LoadingStatusEnum.NotLoaded:
        return 'Ej lastad';
      case LoadingStatusEnum.Loaded:
        return 'Lastad';
      case LoadingStatusEnum.Unloaded:
        return 'Lossad';
      default:
        return '';
    }
  };

  return (
    <SegmentItemWrapper ref={setNodeRef} style={style} isDragging={isDragging}>
      <DragHandle {...attributes} {...listeners}>
        <FontAwesomeIcon icon={faGripVertical} />
      </DragHandle>
      <SegmentContent>
        <SegmentHeader>
          <SegmentTitle>Segment {segment.segmentOrderNumber}</SegmentTitle>
          <StatusBadge status={segment.segmentStatus}>
            {getStatusText(segment.segmentStatus)}
          </StatusBadge>
        </SegmentHeader>
        <RouteInfo>
          {segment.fromCity} → {segment.toCity}
        </RouteInfo>
        Bilar:
        {segment.cars && segment.cars.length > 0 && (
          <CarsList>
            {segment.cars.map((car) => {
              const vehicleName = [car.vehicleBrand, car.vehicleModel]
                .filter(Boolean)
                .join(' ');
              return (
                <CarItem key={car.id}>
                  <CarInfo>
                    <RegNumber>{car.caseRegistrationNumber}</RegNumber>
                    {vehicleName && <VehicleName>{vehicleName}</VehicleName>}
                  </CarInfo>
                  <LoadingStatusBadge status={car.loadingStatus}>
                    {getLoadingStatusText(car.loadingStatus)}
                  </LoadingStatusBadge>
                </CarItem>
              );
            })}
          </CarsList>
        )}
      </SegmentContent>
    </SegmentItemWrapper>
  );
};

interface ViewSegmentsModalProps {
  assignmentId: number;
  onClose: () => void;
}

const ViewSegmentsModal: React.FC<ViewSegmentsModalProps> = ({
  assignmentId,
  onClose,
}) => {
  const segmentsQuery = useTransportSegmentsGetSegmentsForTruckAssignment(
    { truckAssignmentId: assignmentId },
    {
      query: {
        enabled: !!assignmentId,
      },
    }
  );

  const updateSegmentOrderMutation = useTransportSegmentsUpdateSegmentOrder();

  const [segments, setSegments] = useState<TransportSegmentModel[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Update local segments when data is loaded
  React.useEffect(() => {
    if (segmentsQuery.data) {
      setSegments([...segmentsQuery.data].sort((a, b) => a.segmentOrderNumber - b.segmentOrderNumber));
    }
  }, [segmentsQuery.data]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSegments((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newItems = [...items];
        const [movedItem] = newItems.splice(oldIndex, 1);
        newItems.splice(newIndex, 0, movedItem);

        // Update segment order numbers
        return newItems.map((item, index) => ({
          ...item,
          segmentOrderNumber: index + 1,
        }));
      });
      setHasChanges(true);
    }
  }, []);

  const handleSave = useCallback(async () => {
    try {
      // Update all segment orders in a single batch call
      await updateSegmentOrderMutation.mutateAsync({
        data: segments,
      });

      // Refetch to get fresh data
      await segmentsQuery.refetch();
      setHasChanges(false);
      onClose();
    } catch (error) {
      toast.error('Det gick inte att uppdatera segmentordningen. Försök igen.');
    }
  }, [segments, updateSegmentOrderMutation, segmentsQuery, onClose]);

  const handleCancel = useCallback(() => {
    if (hasChanges) {
      setShowConfirmModal(true);
    } else {
      onClose();
    }
  }, [hasChanges, onClose]);

  const handleConfirmCancel = useCallback(() => {
    setShowConfirmModal(false);
    onClose();
  }, [onClose]);

  if (segmentsQuery.isLoading) {
    return (
      <Modal>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Segment</ModalTitle>
            <CloseButton onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </CloseButton>
          </ModalHeader>
          <div>Laddar segment...</div>
        </ModalContent>
      </Modal>
    );
  }

  if (!segments || segments.length === 0) {
    return (
      <Modal>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Segment</ModalTitle>
            <CloseButton onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </CloseButton>
          </ModalHeader>
          <EmptyState>
            Inga segment hittades för detta uppdrag.
            <br />
            Tilldela bilar till lastbilen för att generera segment.
          </EmptyState>
          <ButtonRow>
            <Button onClick={onClose}>Stäng</Button>
          </ButtonRow>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <>
      <Modal>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Segment ({segments.length})</ModalTitle>
            <CloseButton onClick={handleCancel}>
              <FontAwesomeIcon icon={faTimes} />
            </CloseButton>
          </ModalHeader>

          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={segments.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <SegmentsList>
                {segments.map((segment) => (
                  <SortableSegmentItem key={segment.id} segment={segment} />
                ))}
              </SegmentsList>
            </SortableContext>
          </DndContext>

          <ButtonRow>
            <Button onClick={handleCancel}>
              Avbryt
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanges || updateSegmentOrderMutation.isLoading}
            >
              {updateSegmentOrderMutation.isLoading ? 'Sparar...' : 'Spara ordning'}
            </Button>
          </ButtonRow>
        </ModalContent>
      </Modal>

      {showConfirmModal && (
        <ConfirmModal
          title="Osparade ändringar"
          content="Du har osparade ändringar. Är du säker på att du vill stänga?"
          confirmText="Ja, stäng"
          cancelText="Avbryt"
          onConfirm={handleConfirmCancel}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}
    </>
  );
};

export default ViewSegmentsModal;
